import { useMutation, useQuery } from "@apollo/client";
import { Box, Link as MuiLink, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { xsHeight, mdHeight } from "components/AppBar";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { tabsOffset } from "components/TabLinks";
import { useCanEditFacility } from "hooks/useAuthorization";
import {
  HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES,
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
} from "hooks/useHarmonizedViolations";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { firstUserId, toUserOption } from "../../Violations/violationDefaultAssignees";
import { useFacility } from "../useFacility";

export const FACILITY_VIOLATIONS_SECTION_ID = "facility-violations";
export const FACILITY_VIOLATIONS_SECTION_ICON = "⚠️";

/**
 * Owns the picker's live selection, seeded from the facility's current default.
 * Keyed by facilityId so the selection resets cleanly when the profile switches
 * to another facility (no prop-sync effect), mirroring the violation drawer's
 * keyed-editor pattern. Saves on every change like the assignee override — no
 * separate Save button.
 */
function FacilityDefaultAssigneePicker({
  tenantId,
  facilityId,
  facilityName,
  initial,
  canEdit,
}: {
  tenantId: string;
  facilityId: string;
  facilityName: string;
  initial: UserOption | null;
  canEdit: boolean;
}) {
  const alerts = useAlerts();
  const [selected, setSelected] = useState<UserOption[] | null>(initial ? [initial] : null);
  const [setFacilityDefault, { loading: saving }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
    {
      // A default change re-inherits assignees across this facility's violations,
      // and Apollo can't infer which grid rows, donut buckets, or popover
      // prefills that touches — so refetch all three. They no-op while the
      // violations page is unmounted (Apollo only refetches active queries).
      refetchQueries: [
        HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES,
        HARMONIZED_VIOLATIONS,
        HARMONIZED_VIOLATION_STATS,
      ],
    }
  );

  const handleChange = async (users: UserOption[] | null) => {
    // Snapshot the last-saved value so a failure can roll the field back rather
    // than leave it asserting a default the server rejected.
    const previous = selected;
    setSelected(users);
    const userId = firstUserId(users);
    try {
      const { data } = await setFacilityDefault({ variables: { tenantId, facilityId, userId } });
      // A SAVE answering null with no error is the server declining; a clear
      // answers null by design, so only a save reads null as failure.
      const saved = userId ? Boolean(data?.setHarmonizedViolationFacilityDefaultAssignee) : true;
      if (!saved) {
        setSelected(previous);
        alerts.error("Could not set the default assignee. Try again.");
        return;
      }
      alerts.success(
        userId
          ? `Default assignee set for ${facilityName}`
          : `Default assignee cleared for ${facilityName}`
      );
    } catch (e) {
      setSelected(previous);
      alerts.error(
        userId
          ? "Could not set the default assignee. Try again."
          : "Could not clear the default assignee. Try again.",
        e
      );
    }
  };

  return (
    <Box sx={{ maxWidth: 480 }}>
      <MultipleUserPicker
        label="Default assignee"
        multiple={false}
        value={selected}
        disabled={saving || !canEdit}
        onChange={(users) => void handleChange(users)}
      />
    </Box>
  );
}

/**
 * Facility profile "Violations" section: sets the facility-level default
 * assignee for this facility's ECHO violations. Replaces the per-violation
 * "set facility default" button that used to live in the violation panel.
 */
export const ViolationsSection: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const { data: facilityData } = useFacility();
  // Matches the other editable profile sections: false for a read-only user or
  // an archived facility, so the control is not offered when the write is barred.
  const canEdit = useCanEditFacility();

  const { data, loading, error } = useQuery(HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES, {
    variables: { tenantId: tenantId ?? "", facilityId: facilityId ?? "" },
    skip: !tenantId || !facilityId,
  });

  const facilityName = facilityData?.facility?.name ?? "this facility";
  const initial = toUserOption(data?.harmonizedViolationDefaultAssignees?.facility?.user);

  return (
    <Box
      id={FACILITY_VIOLATIONS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{FACILITY_VIOLATIONS_SECTION_ICON} Violations</Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Set the default assignee for this facility's ECHO violations. Every violation at this
        facility without its own assignee — existing and new — is handled by this person. View all{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/insights/violations`}>
          Violations
        </MuiLink>{" "}
        for this organization.
      </Typography>

      {error && (
        <Typography variant="body2" color="error">
          Could not load the current default assignee. Reload the page to try again.
        </Typography>
      )}
      {!error && loading && (
        <Skeleton variant="rectangular" height={40} sx={{ maxWidth: 480, borderRadius: 1 }} />
      )}
      {!error && !loading && (
        <FacilityDefaultAssigneePicker
          key={facilityId}
          tenantId={tenantId ?? ""}
          facilityId={facilityId ?? ""}
          facilityName={facilityName}
          initial={initial}
          canEdit={canEdit}
        />
      )}
    </Box>
  );
};
